import { memo, useEffect, useState } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import { useDebounce } from "../../../hooks/useDebounce";
import "./Selectors.css";
import { useGetCostCentersQuery } from "../../../features/TaskAutomation/taskAutomationSlice";

const CostCentreSelect = ({
  label,
  value,
  onChange,
  isDisabled = false,
  placeholder = "Select",
}) => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState([]);
  const debounceSearch = useDebounce(query);

  const {
    data: costCentresData,
    error: costCentresError,
    isFetching,
  } = useGetCostCentersQuery({
    query: debounceSearch,
    costCenterKey: "",
    page,
    pageSize,
  });

  useEffect(() => {
    if (costCentresData) {
      const { items } = costCentresData;
      const newOptions = items?.map((costCentre) => ({
        label: `${costCentre.key} - ${costCentre.description}`,
        value: costCentre.id,
      }));

      setOptions((prevOptions) => {
        const seen = new Set();
        const mergedOptions = [
          ...prevOptions,
          ...newOptions,
          ...(value ? [value] : []),
        ].filter((opt) => {
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        });

        return mergedOptions;
      });

      setHasMore(items?.length >= pageSize);
    }

    if (costCentresError) {
      toast.error(costCentresError?.data?.Message || "Failed to load");
    }
  }, [costCentresData, costCentresError, pageSize, value]);

  const handleMenuScrollToBottom = () => {
    if (hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  const selectedOption =
    options.find((opt) =>
      typeof value === "object" && value !== null
        ? opt.value === value.value
        : opt.value === value
    ) || null;

  const handleSearch = (inputValue) => {
    setQuery(inputValue);
    setPage(1);
    if (inputValue) {
      setOptions([]);
    }
  };

  const handleChange = (selected) => {
    onChange(selected || null);
    if (!selected) {
      handleSearch("");
    }
  };

  return (
    <>
      {label && <label className="custom-label">{label}</label>}
      <Select
        className="paginated-selector"
        classNamePrefix="custom-select"
        options={options}
        value={selectedOption}
        isDisabled={isDisabled}
        isClearable={true}
        isLoading={isFetching}
        onChange={handleChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onInputChange={(inputValue, { action }) => {
          if (action === "input-change") {
            handleSearch(inputValue);
          }
        }}
        placeholder={placeholder}
      />
    </>
  );
};

export default memo(CostCentreSelect);
